import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Chip,
  Paper,
  Grid,
  IconButton,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { format, addDays, setHours, setMinutes, isSameDay, startOfDay } from 'date-fns';
import { toast } from 'react-toastify';
import { WidgetConfig, DealershipHours } from '../types';
import {
  parseDealershipHours,
  getAvailableHoursForDate,
  isDealershipOpen,
} from '../utils/dealershipHours';
import WidgetAPI from '../services/api';

interface TimeSlot {
  datetime: Date;
  label: string;
}

interface MilesTimeSlotPickerProps {
  onSelectTimeSlot: (datetimeUTC: string, label: string, rawDate: Date) => void;
  config: WidgetConfig;
  api: WidgetAPI;
}

function MilesTimeSlotPicker({ onSelectTimeSlot, config, api }: MilesTimeSlotPickerProps) {

  const [selectedDate, setSelectedDate] = useState<Date>(addDays(new Date(), 1)); // Start with tomorrow
  const [selectedSlot, setSelectedSlot] = useState<number | null>(null);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [dealershipHours, setDealershipHours] = useState<DealershipHours | null>(null);
  const [isLoadingHours, setIsLoadingHours] = useState<boolean>(true);

  // Fetch dealership hours on mount
  useEffect(() => {
    const fetchDealershipHours = async () => {
      try {
        setIsLoadingHours(true);

        // Fetch dealer information from /dealers/:dealerId
        const dealerData = await api.getDealer();

        // Extract dealershipHours from dealer_settings
        const hoursJson = dealerData?.dealer_settings?.dealershipHours || null;

        const parsedHours = parseDealershipHours(hoursJson);
        setDealershipHours(parsedHours);
      } catch (error) {
        console.warn('[Time Slot Picker] Error fetching dealership hours, using defaults:', error);
        setDealershipHours(parseDealershipHours(null));
      } finally {
        setIsLoadingHours(false);
      }
    };

    fetchDealershipHours();
  }, [api]);

  // Generate time slots for the selected date based on dealership hours
  const generateTimeSlotsForDate = useCallback((date: Date): TimeSlot[] => {
    if (!dealershipHours) {
      return [];
    }

    const slots: TimeSlot[] = [];
    const now = new Date();

    // Check if dealership is open on this date
    if (!isDealershipOpen(dealershipHours, date)) {
      return [];
    }

    // Get available hours for this date
    const availableHours = getAvailableHoursForDate(dealershipHours, date);

    availableHours.forEach(hour => {
      const datetime = setMinutes(setHours(date, hour), 0);

      // Only include slots that are in the future
      if (datetime > now) {
        const label = format(datetime, 'h:mm a');
        slots.push({ datetime, label });
      }
    });

    return slots;
  }, [dealershipHours]);

  // Update time slots when selected date or dealership hours change
  useEffect(() => {
    if (dealershipHours) {
      const slots = generateTimeSlotsForDate(selectedDate);
      setTimeSlots(slots);
      setSelectedSlot(null); // Reset selection when date changes
    }
  }, [selectedDate, dealershipHours, generateTimeSlotsForDate]);

  const handleSlotClick = (index: number) => {
    setSelectedSlot(index);
  };

  const handlePreviousDay = () => {
    const prevDay = addDays(selectedDate, -1);
    const today = startOfDay(new Date());
    // Don't allow going to past dates
    if (prevDay >= today) {
      setSelectedDate(prevDay);
    }
  };

  const handleNextDay = () => {
    const nextDay = addDays(selectedDate, 1);
    setSelectedDate(nextDay);
  };

  const handleConfirm = () => {
    if (selectedSlot !== null) {
      const slot = timeSlots[selectedSlot];
      // Format as "YYYY-MM-DD h:mm A" in user's local timezone for backend validation
      const datetimeString = format(slot.datetime, 'yyyy-MM-dd h:mm a');
      const fullLabel = format(slot.datetime, 'EEE, MMM d \'at\' h:mm a');
      onSelectTimeSlot(datetimeString, fullLabel, slot.datetime);
    } else {
      toast.error("Please select appointment time.");
    }
  };

  const isToday = isSameDay(selectedDate, new Date());
  const canGoPrevious = !isSameDay(selectedDate, new Date());

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        bgcolor: '#f0f7ff',
        borderRadius: 2,
        border: '1px solid #e3f2fd',
      }}
    >
      {/* Header */}
      {/* <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <CalendarTodayIcon sx={{ mr: 1, color: 'primary.main', fontSize: 20 }} />
        <Typography variant="subtitle2" fontWeight={600}>
          Select a time for your test drive
        </Typography>
      </Box> */}

      {/* Date Navigation */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,

          p: 1,

        }}
      >
        <IconButton
          size="small"
          onClick={handlePreviousDay}
          disabled={!canGoPrevious}
          sx={{
            '&:disabled': {
              opacity: 0.3,
            },
          }}
        >
          <ChevronLeftIcon />
        </IconButton>

        <Box sx={{ textAlign: 'center', minWidth: 180 }}>

          {isToday && (
            <Typography variant="caption" color="text.secondary">
              Today
            </Typography>
          )}
          <Typography variant="h6" fontWeight={700}>
            {format(selectedDate, 'MMM d, yyyy')}
          </Typography>
          <Typography variant="body2" fontWeight={600} color="primary">
            {format(selectedDate, 'EEEE')}
          </Typography>
        </Box>

        <IconButton size="small" onClick={handleNextDay}>
          <ChevronRightIcon />
        </IconButton>
      </Box>

      {/* Time Slots */}
      {isLoadingHours ? (
        <Box
          sx={{
            mb: 2,
            p: 3,
            textAlign: 'center',
            bgcolor: 'white',
            borderRadius: 1,
            border: '1px solid #e3f2fd',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Loading available time slots...
          </Typography>
        </Box>
      ) : timeSlots.length > 0 ? (
        <Grid container spacing={1} sx={{ mb: 2 }}>
          {timeSlots.map((slot, index) => (
            <Grid size={6} key={index}>
              <Chip
                icon={<AccessTimeIcon fontSize="small" />}
                label={slot.label}
                onClick={() => handleSlotClick(index)}
                color={selectedSlot === index ? 'primary' : 'default'}
                variant={selectedSlot === index ? 'filled' : 'outlined'}
                sx={{
                  width: '100%',
                  height: 'auto',
                  minHeight: '40px',
                  py: 0.5,
                  px: 1,
                  '& .MuiChip-label': {
                    whiteSpace: 'normal',
                    fontSize: '0.75rem',
                    lineHeight: 1.3,
                  },
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 1,
                  },
                }}
              />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box
          sx={{
            mb: 2,
            p: 3,
            textAlign: 'center',
            bgcolor: 'white',
            borderRadius: 1,
            border: '1px solid #e3f2fd',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {dealershipHours && !isDealershipOpen(dealershipHours, selectedDate)
              ? 'Dealership is closed on this day. Please select another day.'
              : 'No available slots for this date. Please select another day.'}
          </Typography>
        </Box>
      )}

      <Button
        variant="contained"
        fullWidth
        onClick={handleConfirm}
        // disabled={selectedSlot === null}
        startIcon={<CalendarTodayIcon />}
        sx={{
          textTransform: 'none',
          fontWeight: 600,
        }}
      >
        Confirm Appointment
      </Button>
    </Paper>
  );
}

export default MilesTimeSlotPicker;
